// src/components/Auth/EmailVerificationPrompt.jsx

import React, { useState } from "react";
import { FiMail, FiRefreshCcw } from "react-icons/fi";
import toast from "react-hot-toast";
// 🚀 FIX: Use named imports matching auth-api.js exports
import { resendEmailOTP, verifyEmailOTP } from "../api/auth-api"; 

/**
 * Component to handle the email OTP verification step.
 * @param {string} tempToken - The short-lived token from the successful password verification.
 * @param {string} email - The user's email, displayed for context.
 * @param {function} onVerificationSuccess - Callback to proceed to the next step (2FA/TOTP).
 * @param {function} onLogout - Callback to return to the credentials login step.
 */
const EmailVerificationPrompt = ({ tempToken, email, onVerificationSuccess, onLogout }) => {
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  // NOTE: The backend's login function already sends the first code.
  // This function only handles the explicit resend action.
  const handleResend = async () => {
    setResendLoading(true);
    setError("");
    try {
      // 🚀 FIX: Call the function directly
      await resendEmailOTP(tempToken); 
      toast.success("New code sent! Check your inbox.");
    } catch (err) {
      setError(err.message || "Failed to resend code.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!tempToken || otpCode.length !== 6) {
        setError("Please enter a valid 6-digit code.");
        setLoading(false);
        return;
    }

    try {
        // 🚀 FIX: Call the function directly
        const res = await verifyEmailOTP(otpCode, tempToken); 
        
        // Success: Proceed to the next step (2FA/TOTP)
        onVerificationSuccess(res); 

    } catch (err) {
        console.error("Email verification failed:", err);
        setError(err.message || "Invalid email code. Please try again.");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full">
      <div className="flex justify-end">
        <button 
            onClick={onLogout} 
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={loading}
        >
            {/*  */}
        </button>
      </div>
      
      <div className="text-center mb-6">
        <FiMail className="h-10 w-10 text-orange-500 mx-auto mb-2" />
        <h1 className="text-2xl font-semibold text-gray-800">Verify Your Email</h1>
        <p className="text-sm text-gray-600 mt-2">
          A 6-digit verification code has been sent to {email}.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
            <div className="text-red-600 bg-red-100 border border-red-300 text-sm px-4 py-2 rounded-md text-center">
              {error}
            </div>
        )}

        <div>
          <label htmlFor="otpCode" className="sr-only">
            Verification Code
          </label>
          <input
            id="otpCode"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            placeholder="123456"
            required
            autoFocus
            className="w-full px-4 py-3 text-center text-xl border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 tracking-widest font-mono"
            disabled={loading}
            maxLength={6}
          />
        </div>

        <button
          type="submit"
          disabled={loading || otpCode.length !== 6}
          className="w-full bg-orange-500 text-white py-2 rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? "Verifying..." : "Verify Code"}
        </button>
        
        <div className="text-center pt-2">
            <button
              type="button"
              onClick={handleResend}
              disabled={loading || resendLoading}
              className="text-sm text-gray-500 hover:text-orange-600 transition flex items-center mx-auto"
            >
              {resendLoading ? (
                  <>
                    <FiRefreshCcw className="h-4 w-4 mr-1 animate-spin" /> Sending...
                  </>
              ) : (
                  <>
                    <FiRefreshCcw className="h-4 w-4 mr-1" /> Resend Code
                  </>
              )}
            </button>
        </div>
      </form>
    </div>
  );
};

export default EmailVerificationPrompt;